import React, { Component } from 'react';
import { connect } from 'react-redux';
import { DebounceInput } from 'react-debounce-input';
import classnames from 'classnames';
import { getNthKeyValue, updateNthKeyValue, getLocalTimeZone, hasHBF } from 'common/utils/helpers';
import { IconLoader } from 'common/IconLoader';
import { ReactComponent as IconSpinnerSmall } from 'imgs/icons/windows/icon_spinner_small.svg';
import WarningBox from 'components/shared/WarningBox/WarningBox';
import {
  directionMetrics,
  gainMetrics,
  generateDirectionDataRows,
  generateGainDataRows,
  TABLE_TYPES,
  RF_CONTROL_WARNING_MESSAGE
} from './utils';
import { enableCommissionActionForGa2 } from '../ProvisionModal/utils';
import SvgAF from 'components/shared/SvgAF/SvgAF';
import RfControllerOverlay from './components/RfControllerOverlay/RfControllerOverlay';
import RfControllerDirectionTable from './components/RfControllerDirectionTable/RfControllerDirectionTable';
import RfControllerGainTable from './components/RfControllerGainTable/RfControllerGainTable';
import RfControllerButtonRow from './components/RfControllerButtonRow/RfControllerButtonRow';
import './RfController.css';

class RfController extends Component {
  state = {
    gainInProgress: false,
    directionInProgress: false,
    activeDirectionTableIndex: null,
    activeGainTableIndex: null,
    renderKey: 0,
    selectedDirectionInputRowIndex: null, //used to maintain focus on input boxes during rerenders
    selectedGainInputRowIndex: null, //used to maintain focus on input boxes during rerenders

    rasterError: false,
    rasterErrorMessage: '',

    rfUIDirectionStatuses: {
      azimuthStatus: 'UNVISITED',
      elevationStatus: 'UNVISITED'
    },

    rfUIGainStatuses: {
      hUplinkGainStatus: 'UNVISITED',
      hDownlinkGainStatus: 'UNVISITED',
      vUplinkGainStatus: 'UNVISITED',
      vDownlinkGainStatus: 'UNVISITED'
    },

    newAzimuth: null,
    newElevation: null,
    newHUplinkGain: null,
    newHDownlinkGain: null,
    newVUplinkGain: null,
    newVDownlinkGain: null,

    gainPermissions: false,

    hasError: false
  };

  constructor(props) {
    super(props);
    this.renderDirectionInput = this.renderDirectionInput.bind(this);
    this.renderGainInput = this.renderGainInput.bind(this);
    this.setRasterError = this.setRasterError.bind(this);
    this.clearInputState = this.clearInputState.bind(this);
    this.selectedDirectionInputRowIndex = null;
    this.selectedGainInputRowIndex = null;

    this.inputsCompleted = null;
    this.inputsDisabled = false;

    this.directionTableColumns = [
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip="PARAMETER">
            Parameter
          </div>
        ),
        headerClassName: 'RfCellMedium',
        className: 'RfCellMedium',
        sortable: false,
        accessor: 'parameter',
        Cell: (row) => (
          <div className="RfController_cellWrapper" data-tip={row.value.hoverMessage}>
            {row.value.name}
          </div>
        )
      },
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip={'MIN/MAX'}>
            MIN/MAX
          </div>
        ),
        headerClassName: 'RfControllerHeader RfCellMedium',
        className: 'RfCellMedium RfCellCenter',
        accessor: 'minMax',
        sortable: false
      },
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip={'CURRENT VALUE'}>
            CURRENT VALUE
          </div>
        ),
        headerClassName: 'RfControllerHeader RfCellMedium',
        className: 'RfCellMedium RfCellCenter',
        accessor: 'currentValue',
        sortable: false
      },
      {
        Header: () => {
          let localTimeZone = getLocalTimeZone();
          let hoverTitle = `Last updated on ${localTimeZone.abbr}`;
          return (
            <div className="RfControllerHeader RfCellMedium RfController_lastUpdatedHeader">
              <div className="RfController_lastUpdatedHeader_PaddingDiv" />
              <div className="RfController_cellWrapper" data-tip={hoverTitle}>
                Last updated on &nbsp;
              </div>
              <div>(</div>
              <div className="RfController_lastUpdatedHeaderZone" data-tip={hoverTitle}>
                {localTimeZone.abbr}
              </div>
              <div>)</div>
              <SvgAF
                img={IconLoader.icon_help_xs}
                hover={IconLoader.icon_help_xs_hover}
                tooltip={localTimeZone.full + ' - your local timezone'}
              />
              <div className="RfController_Header_ResizerSliderIcon">
                <div className="RfController_Header_ResizerSliderIcon_Bar"></div>
              </div>
              <div className="RfController_lastUpdatedHeader_PaddingDiv" />
            </div>
          );
        },
        headerClassName: 'RfControllerHeader RfCellMedium',
        className: 'RfCellMedium RfCellCenter',
        accessor: 'lastUpdated',
        sortable: false,
        Cell: (row) => (
          <div className="RfController_cellWrapper" data-tip={row.value}>
            {row.value}
          </div>
        )
      },
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip={'New Value'}>
            New Value
          </div>
        ),
        headerClassName: 'RfControllerHeader RfCellLarge',
        className: 'RfCellLarge  RfCellCenter',
        sortable: false,
        Cell: (row) => {
          return this.renderDirectionInput(row);
        }
      }
    ];

    this.gainTableColumns = [
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip="PARAMETER">
            Parameter
          </div>
        ),
        accessor: 'parameter',
        headerClassName: 'RfCellMedium',
        className: 'RfCellMedium',
        sortable: false
      },
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip={'MIN/MAX'}>
            MIN/MAX
          </div>
        ),
        headerClassName: 'RfControllerHeader RfCellMedium',
        className: 'RfCellMedium RfCellCenter',
        accessor: 'minMax',
        sortable: false
      },
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip={'CURRENT VALUE'}>
            CURRENT VALUE
          </div>
        ),
        headerClassName: 'RfControllerHeader RfCellMedium RfCellWrapper',
        className: 'RfCellMedium RfCellCenter RfCellWrapper',
        accessor: 'currentValue',
        sortable: false
      },
      {
        headerClassName: 'RfControllerHeader RfCellMedium',
        className: 'RfCellMedium RfCellCenter',
        accessor: 'lastUpdated',
        sortable: false,
        Header: () => {
          let localTimeZone = getLocalTimeZone();
          let hoverTitle = `Last updated on ${localTimeZone.abbr}`;
          return (
            <div className="RfControllerHeader RfCellMedium RfController_lastUpdatedHeader">
              <div className="RfController_lastUpdatedHeader_PaddingDiv" />
              <div data-tip={hoverTitle}>Last updated on &nbsp;</div>
              <div>(</div>
              <div className="RfController_lastUpdatedHeaderZone" data-tip={hoverTitle}>
                {localTimeZone.abbr}
              </div>
              <div>)</div>
              <SvgAF
                img={IconLoader.icon_help_xs}
                hover={IconLoader.icon_help_xs_hover}
                tooltip={localTimeZone.full + ' - your local timezone'}
              />
              <div className="RfController_Header_ResizerSliderIcon">
                <div className="RfController_Header_ResizerSliderIcon_Bar"></div>
              </div>
              <div className="RfController_lastUpdatedHeader_PaddingDiv" />
            </div>
          );
        },
        Cell: (row) => (
          <div className="RfController_cellWrapper" data-tip={row.value}>
            {row.value}
          </div>
        )
      },
      {
        Header: (
          <div className="RfCellTextOverflow" data-tip={'New Value'}>
            New Value
          </div>
        ),
        headerClassName: 'RfControllerHeader RfCellLarge',
        className: 'RfCellLarge RfCellCenter',
        id: 'newGainValue',
        accessor: 'newGainValue',
        sortable: false,
        Cell: (row) => {
          return this.renderGainInput(row);
        }
      }
    ];

    this.gainTableServiceColumns = this.gainTableColumns.map((col) => {
      return { ...col };
    });
  }

  componentDidMount() {
    if (!this.state.gainPermissions && this.props.permissions && this.props.permissions.includes('cm_set_gain')) {
      this.setState({ gainPermissions: true });
    }

    if (this.props.device) {
      this.createDirectionDataRows();
      this.createGainDataRows();
      this.hasRfControllerInvalidInput();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isGa2 && prevProps.device !== this.props.device) {
      this.inputsDisabled = !enableCommissionActionForGa2(this.props.device);
    }

    if (this.props.device !== prevProps.device) {
      this.createDirectionDataRows();
      this.createGainDataRows();
      this.hasRfControllerInvalidInput();
    }
  }

  setRasterError(enable, msg) {
    this.setState({ rasterError: enable, rasterErrorMessage: msg });
  }

  clearInputState() {
    this.setState({
      rfUIDirectionStatuses: {
        azimuthStatus: 'UNVISITED',
        elevationStatus: 'UNVISITED'
      },
      rfUIGainStatuses: {
        hUplinkGainStatus: 'UNVISITED',
        hDownlinkGainStatus: 'UNVISITED',
        vUplinkGainStatus: 'UNVISITED',
        vDownlinkGainStatus: 'UNVISITED'
      },
      newVUplinkGain: '',
      newHUplinkGain: '',
      newVDownlinkGain: '',
      newHDownlinkGain: '',
      newAzimuth: '',
      newElevation: ''
    });
  }

  checkValidInput(rowIndex, inputVal, tableType) {
    let errorStatus = 'VALID';
    let rasterForceVal = 361;

    if (isNaN(inputVal)) {
      errorStatus = 'ERROR_NAN';
    }

    if (tableType === TABLE_TYPES.DIRECTION) {
      if (inputVal > this.directionData[rowIndex].max && inputVal !== rasterForceVal) {
        errorStatus = 'ERROR_MAX';
      }
      if (inputVal < this.directionData[rowIndex].min && inputVal !== rasterForceVal) {
        errorStatus = 'ERROR_MIN';
      }
    }

    if (tableType === TABLE_TYPES.GAIN && !(this.gainData[rowIndex].max === 0 && this.gainData[rowIndex].min === 0)) {
      if (inputVal > this.gainData[rowIndex].max) {
        errorStatus = 'ERROR_MAX';
      }
      if (inputVal < this.gainData[rowIndex].min) {
        errorStatus = 'ERROR_MIN';
      }
    }

    return errorStatus;
  }

  createDirectionDataRows() {
    this.directionData = generateDirectionDataRows(this.props.device);
    this.forceUpdate();
  }

  createGainDataRows() {
    this.gainData = generateGainDataRows(this.props.device);
    this.forceUpdate();
  }

  hasRfControllerInvalidInput() {
    const { rfUIDirectionStatuses, rfUIGainStatuses, hasError } = this.state;
    const ERRORS = ['ERROR_NAN', 'ERROR_MIN', 'ERROR_MAX'];
    let foundError = false;

    Object.keys(rfUIDirectionStatuses).forEach((key) => {
      const curStatus = rfUIDirectionStatuses[key];
      if (ERRORS.includes(curStatus)) {
        foundError = true;
      }
    });

    Object.keys(rfUIGainStatuses).forEach((key) => {
      const curStatus = rfUIGainStatuses[key];
      if (ERRORS.includes(curStatus)) {
        foundError = true;
      }
    });

    if (hasError !== foundError) {
      this.setState({ hasError: foundError });
    }
  }

  checkRowErrorStatus(index, tableType) {
    let targetRfUIStatuses = null;

    if (tableType === TABLE_TYPES.DIRECTION) {
      targetRfUIStatuses = this.state.rfUIDirectionStatuses;
    }
    if (tableType === TABLE_TYPES.GAIN) {
      targetRfUIStatuses = this.state.rfUIGainStatuses;
    }
    const currStatus = getNthKeyValue(targetRfUIStatuses, index);

    let hasError =
      currStatus !== 'UNVISITED' && currStatus !== 'VISITED' && currStatus !== 'LEFT' && currStatus !== 'VALID';

    return hasError;
  }

  checkRowStatus(index, tableType) {
    let targetRfUIStatuses = null;

    if (tableType === TABLE_TYPES.DIRECTION) {
      targetRfUIStatuses = this.state.rfUIDirectionStatuses;
    }

    if (tableType === TABLE_TYPES.GAIN) {
      targetRfUIStatuses = this.state.rfUIGainStatuses;
    }
    return getNthKeyValue(targetRfUIStatuses, index);
  }

  renderDirectionInput(row) {
    let canWrite = false;
    //can't change direction for offline devices or SUs (TODO - enable set direction for hbf SUs)
    if (this.props.device.connectionState === 'Connected') {
      canWrite = true;
    }

    if (this.state.directionInProgress && this.getInputValues(row, TABLE_TYPES.DIRECTION)) {
      return (
        <div>
          <IconSpinnerSmall className="RF_Table_Status_LoadingIcon" />
        </div>
      );
    } else if (canWrite) {
      return (
        <div
          className={classnames({
            'RfInput RfCellLarge':
              !(this.checkRowStatus(row.index, TABLE_TYPES.DIRECTION) === 'VISITED') &&
              !this.checkRowErrorStatus(row.index, TABLE_TYPES.DIRECTION),
            RfCellFocusBox: this.checkRowStatus(row.index, TABLE_TYPES.DIRECTION) === 'VISITED',
            RfCellErrorBox: this.checkRowErrorStatus(row.index, TABLE_TYPES.DIRECTION),
            RfInput_Disabled: this.inputsDisabled
          })}
        >
          <DebounceInput
            className={classnames({ DebounceInput: true })}
            debounceTimeout={200}
            disabled={this.inputsDisabled}
            inputRef={row.index === this.selectedDirectionInputRowIndex ? (input) => input && input.focus() : null}
            type="text"
            value={this.getInputValues(row, TABLE_TYPES.DIRECTION) || ''}
            onFocus={() => {
              this.setState({ activeDirectionTableIndex: row.index });

              this.checkRowStatus(row.index, TABLE_TYPES.DIRECTION) === 'UNVISITED' &&
                this.setState(
                  {
                    rfUIDirectionStatuses: updateNthKeyValue(
                      { ...this.state.rfUIDirectionStatuses },
                      row.index,
                      'VISITED'
                    )
                  },
                  () => (this.inputsCompleted = false)
                );
            }}
            onBlur={(event) => {
              let currStatus = this.checkRowStatus(row.index, TABLE_TYPES.DIRECTION);
              let currValue = this.getInputValues(row, TABLE_TYPES.DIRECTION);

              (currStatus === 'VALID' || currStatus === 'VISITED' || currValue == null || currValue == '') &&
                this.setState(
                  {
                    rfUIDirectionStatuses: updateNthKeyValue({ ...this.state.rfUIDirectionStatuses }, row.index, 'LEFT')
                  },
                  () => (this.inputsCompleted = false)
                );

              if (this.selectedDirectionInputRowIndex === row.index) {
                this.selectedDirectionInputRowIndex = null;
              }
            }}
            onChange={(evt) => {
              this.updateNewValues(row, evt.target.value, TABLE_TYPES.DIRECTION);
              this.selectedDirectionInputRowIndex = row.index;
              let errorStatus = this.checkValidInput(row.index, parseFloat(evt.target.value), TABLE_TYPES.DIRECTION);
              this.inputsCompleted &&
                this.setState(
                  {
                    rfUIDirectionStatuses: updateNthKeyValue(
                      { ...this.state.rfUIDirectionStatuses },
                      row.index,
                      errorStatus
                    )
                  },
                  () => this.state.inputsCompleted && this.setState({ inputsCompleted: false })
                );
            }}
          />
        </div>
      );
    } else {
      return <div />;
    }
  }

  renderGainInput(row) {
    const canWrite = this.props.device.connectionState === 'Connected' && this.state.gainPermissions;

    if (this.state.gainInProgress && this.getInputValues(row, TABLE_TYPES.GAIN)) {
      return (
        <div>
          <IconSpinnerSmall className="RF_Table_Status_LoadingIcon" />
        </div>
      );
    } else if (canWrite) {
      return (
        <div
          className={classnames({
            'RfInput RfCellLarge':
              !(this.checkRowStatus(row.index, TABLE_TYPES.GAIN) === 'VISITED') &&
              !this.checkRowErrorStatus(row.index, TABLE_TYPES.GAIN),
            RfCellFocusBox: this.checkRowStatus(row.index, TABLE_TYPES.GAIN) === 'VISITED',
            RfCellErrorBox: this.checkRowErrorStatus(row.index, TABLE_TYPES.GAIN),
            RfInput_Disabled: this.inputsDisabled
          })}
        >
          <DebounceInput
            className={classnames({ DebounceInput: true })}
            debounceTimeout={200}
            disabled={this.inputsDisabled}
            inputRef={row.index === this.selectedGainInputRowIndex ? (input) => input && input.focus() : null}
            type="text"
            value={this.getInputValues(row, TABLE_TYPES.GAIN) || ''}
            onFocus={() => {
              this.setState({ activeGainTableIndex: row.index });
              this.setState({ renderKey: this.state.renderKey++ });

              this.checkRowStatus(row.index, TABLE_TYPES.GAIN) === 'UNVISITED' &&
                this.setState(
                  { rfUIGainStatuses: updateNthKeyValue({ ...this.state.rfUIGainStatuses }, row.index, 'VISITED') },
                  () => (this.inputsCompleted = false)
                );
            }}
            onBlur={(event) => {
              let currStatus = this.checkRowStatus(row.index, TABLE_TYPES.GAIN);
              let currValue = this.getInputValues(row, TABLE_TYPES.GAIN);
              (currStatus === 'VALID' || currStatus === 'VISITED' || currValue == null || currValue == '') &&
                this.setState(
                  { rfUIGainStatuses: updateNthKeyValue({ ...this.state.rfUIGainStatuses }, row.index, 'LEFT') },
                  () => (this.inputsCompleted = false)
                );

              if (this.selectedGainInputRowIndex === row.index) {
                this.selectedGainInputRowIndex = null;
              }
            }}
            onChange={(evt) => {
              this.updateNewValues(row, evt.target.value, TABLE_TYPES.GAIN);
              this.selectedGainInputRowIndex = row.index;
              let errorStatus = this.checkValidInput(row.index, parseFloat(evt.target.value), TABLE_TYPES.GAIN);
              this.inputsCompleted &&
                this.setState(
                  { rfUIGainStatuses: updateNthKeyValue({ ...this.state.rfUIGainStatuses }, row.index, errorStatus) },
                  () => this.state.inputsCompleted && this.setState({ inputsCompleted: false })
                );
            }}
          />
        </div>
      );
    } else {
      return <div />;
    }
  }

  getInputValues(row, tableID) {
    if (tableID === TABLE_TYPES.DIRECTION) {
      switch (directionMetrics[row.index].id) {
        case 'beamAzimuth':
          return this.state.newAzimuth;
        case 'beamElevation':
          return this.state.newElevation;
        default:
          break;
      }
    }

    if (tableID === TABLE_TYPES.GAIN) {
      switch (gainMetrics[row.index].id) {
        case 'HDL_Gain':
          return this.state.newHDownlinkGain;
        case 'HUL_Gain':
          return this.state.newHUplinkGain;
        case 'VDL_Gain':
          return this.state.newVDownlinkGain;
        case 'VUL_Gain':
          return this.state.newVUplinkGain;
        default:
          break;
      }
    }
  }

  updateNewValues(row, val, tableID) {
    if (tableID === TABLE_TYPES.DIRECTION) {
      switch (directionMetrics[row.index].id) {
        case 'beamAzimuth':
          this.setState({ newAzimuth: val }, () => (this.inputsCompleted = true));
          break;
        case 'beamElevation':
          this.setState({ newElevation: val }, () => (this.inputsCompleted = true));
          break;
        default:
          break;
      }
    }

    if (tableID === TABLE_TYPES.GAIN) {
      switch (gainMetrics[row.index].id) {
        case 'HDL_Gain':
          this.setState({ newHDownlinkGain: val }, () => (this.inputsCompleted = true));
          break;
        case 'HUL_Gain':
          this.setState({ newHUplinkGain: val }, () => (this.inputsCompleted = true));
          break;
        case 'VDL_Gain':
          this.setState({ newVDownlinkGain: val }, () => (this.inputsCompleted = true));
          break;
        case 'VUL_Gain':
          this.setState({ newVUplinkGain: val }, () => (this.inputsCompleted = true));
          break;
        default:
          break;
      }
    }
  }

  handleOverlayClose = () => {
    this.setState({ rasterError: false, rasterErrorMessage: '' });
  };

  handleActionInProgress = (tableType, isInProgress) => {
    if (tableType === TABLE_TYPES.DIRECTION) {
      this.setState({ directionInProgress: isInProgress });
    }
    if (tableType === TABLE_TYPES.GAIN) {
      this.setState({ gainInProgress: isInProgress });
    }
  };

  handleResetActionInputs = () => {
    this.setState({
      gainInProgress: false,
      newVUplinkGain: '',
      newHUplinkGain: '',
      newVDownlinkGain: '',
      newHDownlinkGain: '',
      directionInProgress: false,
      newAzimuth: '',
      newElevation: ''
    });
  };

  render() {
    if (!this.props.device) {
      return null;
    }

    const showDirectionTable = hasHBF(this.props.device);

    return (
      <div className="RfContainer">
        {this.state.rasterError && (
          <RfControllerOverlay
            rasterErrorMessage={this.state.rasterErrorMessage}
            handleOverlayClose={this.handleOverlayClose}
          />
        )}

        <form id="RfControllerForm" className="RfController">
          {showDirectionTable && (
            <RfControllerDirectionTable data={this.directionData} columns={this.directionTableColumns} />
          )}

          <RfControllerGainTable
            data={this.gainData}
            showDirectionTable={showDirectionTable}
            columns={showDirectionTable ? this.gainTableColumns : this.gainTableServiceColumns}
          />
          <div className="RfControllerWarningContainer">
            <WarningBox warningMessages={RF_CONTROL_WARNING_MESSAGE} />
          </div>

          <RfControllerButtonRow
            device={this.props.device}
            newAzimuth={this.state.newAzimuth}
            newElevation={this.state.newElevation}
            newVUplinkGain={this.state.newVUplinkGain}
            newHUplinkGain={this.state.newHUplinkGain}
            newVDownlinkGain={this.state.newVDownlinkGain}
            newHDownlinkGain={this.state.newHDownlinkGain}
            gainInProgress={this.state.gainInProgress}
            directionInProgress={this.state.directionInProgress}
            hasError={this.state.hasError}
            clearInputState={this.clearInputState}
            handleActionInProgress={this.handleActionInProgress}
            handleResetActionInputs={this.handleResetActionInputs}
          />
        </form>
      </div>
    );
  }
}

export default connect((store) => {
  return {
    permissions: store.data.rolePermissions.permissions,
    page: store.page
  };
})(RfController);
